import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * A side drawer that fetches and renders product content for quick add.
 * Opens on `quickadd-drawer:open` custom events dispatched on `document`.
 *
 * @extends {DialogComponent}
 */
class QuickAddDrawer extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('quickadd-drawer:open', this.#handleOpen);
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('quickadd-drawer:open', this.#handleOpen);
    this.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#abortController?.abort();
  }

  get #inner() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.quick-add-drawer__inner'));
  }

  #handleOpen = async (/** @type {CustomEvent} */ event) => {
    const { productUrl } = event.detail;
    const inner = this.#inner;
    if (!inner) return;

    inner.innerHTML = '<div class="quick-add-drawer__loading" aria-label="Loading" role="status"></div>';
    this.showDialog();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Inject any stylesheets from the product page that aren't already loaded
      const loadedStylesheets = new Set(
        [...document.querySelectorAll('link[rel="stylesheet"][href]')].map((l) => /** @type {HTMLLinkElement} */(l).href)
      );
      const stylePromises = [];
      html.querySelectorAll('link[rel="stylesheet"][href]').forEach((el) => {
        const href = el.getAttribute('href') ?? '';
        const absoluteHref = new URL(href, location.href).href;
        if (!loadedStylesheets.has(absoluteHref)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          const promise = new Promise((resolve) => {
            link.onload = resolve;
            link.onerror = resolve;
          });
          document.head.appendChild(link);
          stylePromises.push(promise);
        }
      });

      // Inject any module scripts from the product page that aren't already loaded
      html.querySelectorAll('script[src][type="module"]').forEach((el) => {
        const src = el.getAttribute('src') ?? '';
        if (!document.querySelector(`script[src="${src}"]`)) {
          const script = document.createElement('script');
          script.src = src;
          script.type = 'module';
          document.head.appendChild(script);
        }
      });

      // Wait for newly added stylesheets before inserting content to avoid FOUC
      if (stylePromises.length) await Promise.all(stylePromises);

      const content = html.querySelector('[data-product-grid-content]');
      if (content) {
        // Wrap in .product-information so scoped CSS selectors (e.g. highlights, awards)
        // that use `.product-information .x` as an ancestor still match inside the drawer.
        const wrapper = document.createElement('div');
        wrapper.className = 'product-information';
        wrapper.appendChild(content);
        inner.innerHTML = '';
        inner.appendChild(wrapper);
        this.#interceptLinkedSizeNavigation(inner);
        this.#linkifyProductTitle(inner, productUrl);
        this.#insertViewDetailsLink(inner, productUrl);
        this.#initRechargeWidget(inner);
      }
    } catch (err) {
      if (/** @type {Error} */ (err).name !== 'AbortError') console.warn('[quick-add-drawer]', err);
    } finally {
      this.#abortController = null;
    }
  };

  /**
   * Replaces inline onclick navigation on linked-size inputs with a drawer re-open.
   * Without this, clicking a size linked to a different product navigates the page.
   * @param {HTMLElement} container
   */
  #interceptLinkedSizeNavigation(container) {
    container.querySelectorAll('input[data-linked-product-url]').forEach((el) => {
      const input = /** @type {HTMLInputElement} */ (el);
      input.removeAttribute('onclick');
      input.addEventListener('change', () => {
        const relativeUrl = input.dataset.linkedProductUrl;
        if (!relativeUrl) return;
        const productUrl = new URL(relativeUrl, location.href).href;
        document.dispatchEvent(new CustomEvent('quickadd-drawer:open', { detail: { productUrl } }));
      });
    });
  }

  /**
   * Wraps the product title heading's contents in a link to the full PDP.
   * @param {HTMLElement} container
   * @param {string} productUrl
   */
  #linkifyProductTitle(container, productUrl) {
    const heading = container.querySelector('.product-details h1');
    if (!heading || heading.querySelector('a')) return;

    const link = document.createElement('a');
    link.className = 'quick-add-drawer__title-link';
    link.href = productUrl;
    link.append(...heading.childNodes);
    heading.appendChild(link);
  }

  /**
   * Reinitializes the Recharge subscription widget after dynamic content injection.
   * The widget is a web component whose connectedCallback only fires once per DOM insertion.
   * Clone-and-replace forces re-initialization; inline scripts are also re-executed
   * since script tags injected via innerHTML/appendChild don't run automatically.
   * @param {HTMLElement} container
   */
  #initRechargeWidget(container) {
    container.querySelectorAll('script:not([src]):not([type="application/json"]):not([type="application/ld+json"])').forEach((el) => {
      const clone = document.createElement('script');
      Array.from(el.attributes).forEach((attr) => clone.setAttribute(attr.name, attr.value));
      clone.textContent = el.textContent;
      el.parentNode?.replaceChild(clone, el);
    });

    const widget = container.querySelector('recharge-subscription-widget');
    if (!widget?.parentNode) return;

    customElements.whenDefined('recharge-subscription-widget').then(() => {
      widget.parentNode?.replaceChild(widget.cloneNode(true), widget);
      document.dispatchEvent(new CustomEvent('quickadd-drawer:recharge-widget-rendered'));
    });
  }

  /**
   * Inserts a "View Details" link linking to the full PDP above the purchase controls —
   * above the Recharge subscription widget (whose first option is the one-time purchase)
   * when the product has one, otherwise above the plain buy-buttons block.
   * @param {HTMLElement} container
   * @param {string} productUrl
   */
  #insertViewDetailsLink(container, productUrl) {
    const anchor = container.querySelector('recharge-subscription-widget') ?? container.querySelector('.buy-buttons-block');
    if (!anchor?.parentNode || anchor.previousElementSibling?.classList.contains('quick-add-drawer__view-details-link')) return;

    const link = document.createElement('a');
    link.className = 'quick-add-drawer__view-details-link';
    link.href = productUrl;
    link.textContent = this.dataset.viewDetailsText || 'View Details';
    anchor.parentNode.insertBefore(link, anchor);
  }

  #handleCartUpdate = (/** @type {CustomEvent} */ event) => {
    if (!event.detail?.data?.didError) {
      const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));

      if (this.refs.dialog?.open && cartDrawer) {
        // Temporarily remove auto-open so the CartAddEvent bubbling to document
        // doesn't open the cart drawer mid-animation (which would conflict with
        // closeDialog's body-scroll-lock cleanup). Restore it after close.
        const hadAutoOpen = cartDrawer.hasAttribute('auto-open');
        if (hadAutoOpen) cartDrawer.removeAttribute('auto-open');

        this.addEventListener(
          DialogCloseEvent.eventName,
          () => {
            if (hadAutoOpen) cartDrawer.setAttribute('auto-open', '');
            cartDrawer.open();
          },
          { once: true }
        );
      }

      this.closeDialog();
    }
  };
}

if (!customElements.get('quick-add-drawer')) {
  customElements.define('quick-add-drawer', QuickAddDrawer);
}
